import * as React from "react";
import { 
  Grid, 
  Link, 
  Switch, 
  Stack,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CreateIcon from "@mui/icons-material/Create";
import ArchiveIcon from "@mui/icons-material/Archive";
import DeleteIcon from "@mui/icons-material/Delete";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import { snakeCaseToTitleCase } from "../utils/CaseFormatHelper";

// components
import { Modal as DeleteModal } from "../modals/Modal";

// settings
import { getHeaders } from "../utils/HeaderHelper";
import { AuthenticationContext } from "../Context";

// helper
import { toTitleCase } from "../utils/CaseFormatHelper";
import { formatToUsDate } from "../utils/DateHelper";

// hooks
import { useCustomerPermission } from "../hooks/useCustomerPermission";

interface Props {}

const TemplatesList: React.FC<Props> = (props: any) => {
  const authenticationSetting = React.useContext(AuthenticationContext);

  const [showArchiveModal, setShowArchiveModal] =
    React.useState<boolean>(false);
  const [deleteTemplate, setDeleteTemplate] = React.useState<any>();

  // questionnaire states
  const [allQuestionnaires, setAllQuestionnaires] = React.useState<any>([]);
  const [questionnaires, setQuestionnaires] = React.useState<any>([]);
  const [templates, setTemplates] = React.useState<any>([]);
  const [showArchived, setShowArchived] = React.useState<boolean>(false);
  const { permitted: tabletPermitted } = useCustomerPermission(
    "Allow questionnaires to display on local device"
  );

  // fetching inital data
  React.useEffect(() => {
    getData();
  }, []);

  const getData = () => {
    fetch(`/data_fetching/notes_templates`, {
      method: "GET",
      headers: getHeaders(authenticationSetting.csrfToken),
    })
      .then((result) => result.json())
      .then((result) => {
        if (!result.success) {
          console.log(result.error);
        } else {
          setTemplates(result.resource.notes_templates);
        }
      })
      .catch((error) => {
        console.log(error);
      });
  };

  const handleShowArchieved = () => {
    setShowArchived(!showArchived);
  };

  //Archive handlers
  const handleArchiveBtnClick = (template) => {
    setDeleteTemplate(template);
  };

  React.useEffect(() => {
    if (deleteTemplate?.id) {
      setShowArchiveModal(true);
    }
  }, [deleteTemplate?.id]);

  const handleDelete = () => {
    if (deleteTemplate?.id) {
      fetch(`/notes_templates/${deleteTemplate?.id}`, {
        method: "DELETE",
        headers: getHeaders(authenticationSetting.csrfToken),
      })
        .then((result) => result.json())
        .then((result) => {
          if (result.success == false) {
            alert(result.error);
          } else {
            setDeleteTemplate(undefined);
            setShowArchiveModal(false);
            getData();
          }
        })
        .catch((error) => {
          alert(error);
        });
    }
  };
  const closeModal = () => {
    setDeleteTemplate(undefined);
    setShowArchiveModal(false);
  };

  const publishTemplate = (templateId) => {
    if (templateId) {
      fetch(`/templates_publish/${templateId}`, {
        method: "POST",
        headers: getHeaders(authenticationSetting.csrfToken),
      })
        .then((result) => result.json())
        .then((result) => {
          if (result.success == false) {
            alert(result.error);
          } else {
            getData();
          }
        })
        .catch((error) => {
          alert(error);
        });
    }
  };

  return (
    <div className="main-content-outer">
      <DeleteModal
        successModalOpen={showArchiveModal}
        setSuccessModalOpen={setShowArchiveModal}
        successHeader={`${
          deleteTemplate?.status == "draft" ? "Delete" : "Archive"
        } Template?`}
        successContent={`You are attempting to ${
          deleteTemplate?.status == "draft" ? "delete" : "archive"
        } this Template. This cannot be undone. Would you like to continue?`}
        successCallback={handleDelete}
        closeCallback={closeModal}
        confirmButtonText="Continue"
      />
      <Grid
        container
        direction="row"
        justifyContent="flex-start"
        alignItems="flex-start"
        className="main-content"
        sx={{ paddingLeft: "138px" }}
      >
        <Grid item xs={12} className="patient-edit-container patient-edit-form">
          <Grid container>
            <Grid
              className="patient-edit-header"
              container
              justifyContent="space-between"
            >
              <Grid item xs={4}>
                <Stack direction="row" columnGap="12px" alignItems="center">
                  <Link
                    style={{
                      textDecoration: "none",
                      display: "flex",
                      alignItems: "center",
                    }}
                    href={`/builder-tools`}
                  >
                    <ArrowBackIosNewIcon
                      style={{
                        borderRadius: "3px",
                        padding: "8px",
                        color: "black",
                        boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.2)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: "pointer",
                        transition: "all 0.3s ease",
                        fontSize: "14px",
                      }}
                    />
                  </Link>
                  <p className="secondary-label" style={{ marginLeft: "0px" }}>
                    Encounter Template Builder
                  </p>
                </Stack>
              </Grid>

              <Grid item xs={6} className="q-btn-container">
                <span className="font-16px grey-font">
                  <Switch
                    checked={showArchived}
                    onChange={handleShowArchieved}
                    color="primary"
                  />
                  Show Archived Template
                </span>

                <Link
                  href="/new-note-template"
                  className="grey-font"
                  style={{
                    height: "40px",
                    marginRight: "16px",
                    paddingLeft: "16px",
                    paddingRight: "16px",
                    display: "flex",
                    alignItems: "center",
                    fontSize: "16px",
                    position: "relative",
                    top: "-2px",
                  }}
                >
                  <img
                    src="/assets/menu-track.png"
                    width="20"
                    height="20"
                    alt="Add New Template"
                    style={{ padding: "5px" }}
                  />
                  Add New Template
                </Link>
              </Grid>
            </Grid>
            <Grid container>
              <Grid item xs={12}>
                <TableContainer>
                  <Table size="small" aria-label="a dense table">
                    <TableHead>
                      <TableRow>
                        <TableCell component="th" className="bold-font-face" style={{
                          width: "20%"
                        }}>
                          Title
                        </TableCell>
                        <TableCell component="th" className="bold-font-face" style={{
                          width: "40%"
                        }}>
                          Subtext
                        </TableCell>
                        <TableCell
                          component="th"
                          className="bold-font-face"
                          align="center"
                        >
                          Date Published
                        </TableCell>
                        <TableCell
                          component="th"
                          className="bold-font-face"
                          align="center"
                        >
                          Edit/View
                        </TableCell>
                        <TableCell
                          component="th"
                          className="bold-font-face"
                          align="center"
                        >
                          Archive
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {templates.map(
                        (template, index) =>
                          (showArchived ||
                            template.status !== "archived") && (
                            <TableRow
                              key={template.id}
                              className={
                                index % 2 == 0 && `panel-grey-background`
                              }
                            >
                              <TableCell>
                                {template.name}
                              </TableCell>
                              <TableCell>{template.subtext}</TableCell>
                              <TableCell align="center">
                                {template.published_at ? (
                                  formatToUsDate(template.published_at)
                                ) : (
                                  <div
                                    style={{
                                      display: "flex",
                                      justifyContent: "center",
                                    }}
                                  >
                                    <Link
                                      onClick={() => {
                                        publishTemplate(template.id);
                                      }}
                                      className="basic-button orange-btn"
                                      style={{
                                        width: "100px",
                                      }}
                                    >
                                      Publish
                                    </Link>
                                  </div>
                                )}
                              </TableCell>
                              <TableCell align="center">
                                {(template.status === "published" ||
                                  template.status === "archived") ? (
                                  <Link
                                    href={`/edit-note-template/${template.id}`}
                                    style={{
                                      color: "black",
                                    }}
                                  >
                                    <VisibilityIcon />
                                  </Link>
                                )
                                :
                                (template.status === "draft" && (
                                  <Link
                                    href={`/edit-note-template/${template.id}`}
                                    style={{
                                      color: "black",
                                    }}
                                  >
                                    <CreateIcon />
                                  </Link>
                                ))
                                }
                              </TableCell>
                              <TableCell align="center">
                                {template.status !== "archived" && (
                                  <Link
                                    onClick={() =>
                                      handleArchiveBtnClick(template)
                                    }
                                    style={{
                                      color: "black",
                                    }}
                                  >
                                    {template.status !== "draft" ? (
                                      <ArchiveIcon />
                                    ) : (
                                      <DeleteIcon />
                                    )}
                                  </Link>
                                )}
                              </TableCell>
                            </TableRow>
                          )
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </div>
  );
};

export default TemplatesList;
